/**
 * useActiveRole — Spec 112: Switch Role Support
 * Spec 294 category 3: TanStack Query migration (2026-08-03).
 *
 * Provides the user's effective role within the current hierarchy,
 * supporting dynamic role switching for users with dynamic_role = 'switch'.
 *
 * Returns:
 * - effectiveRole: 'dominant' | 'submissive' — the role to use for UI decisions
 * - dynamicRole: the raw dynamic_role value (null for legacy users)
 * - isSwitch: whether the user is a switch (can toggle roles)
 * - toggleRole(): swap between dominant and submissive (only for switches)
 * - loading: whether the initial fetch is in progress
 */

import { useCallback, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { organizationAPI } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import { getErrorMessage } from '../utils/errorUtils';
import { showError } from '../utils/toast';
import { queryClient } from '../lib/queryClient';
import { unwrapResponse } from '../utils/unwrapResponse';
import { queryKeys } from './queries/queryKeys';

type HelmRole = 'dominant' | 'submissive';
type DynamicRole = 'dominant' | 'submissive' | 'switch' | null;

interface ActiveRoleState {
  effectiveRole: HelmRole;
  dynamicRole: DynamicRole;
  activeRole: HelmRole | null;
  isSwitch: boolean;
  /** Spec 274 item 3 — ISO timestamp the switch designation was granted, or null (never a switch, or unknown). */
  switchSince: string | null;
  loading: boolean;
  error: string | null;
  toggleRole: () => Promise<void>;
  refresh: () => Promise<void>;
}

interface DynamicRoleData {
  dynamicRole: DynamicRole;
  activeRole: HelmRole | null;
  effectiveRole: HelmRole;
  switchSince: string | null;
}

const DEFAULT_DATA: DynamicRoleData = {
  dynamicRole: null,
  activeRole: null,
  effectiveRole: 'submissive',
  switchSince: null,
};

/**
 * A 404 means the switchRole feature isn't enabled for this org/member --
 * this resolves to the defaults (not an error) so `error` stays null,
 * matching the pre-migration hook's 404 branch exactly. Any other failure
 * is rethrown so it lands in TanStack's normal error state.
 */
async function fetchDynamicRole(orgId: string, userId: string): Promise<DynamicRoleData> {
  try {
    const response = await organizationAPI.getDynamicRole(orgId, userId);
    const data = unwrapResponse<{
      dynamicRole: DynamicRole;
      activeRole: HelmRole;
      effectiveRole: HelmRole;
      switchSince?: string | null;
    }>(response.data);
    if (!data) return DEFAULT_DATA;
    return {
      dynamicRole: data.dynamicRole,
      activeRole: data.activeRole,
      effectiveRole: data.effectiveRole,
      switchSince: data.switchSince ?? null,
    };
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number } };
    if (axiosErr?.response?.status === 404) {
      return DEFAULT_DATA;
    }
    throw err;
  }
}

export function useActiveRole(orgId: string | null, userId?: string): ActiveRoleState {
  const { features } = useFeatures();
  // Errors from the toggleRole MUTATION share the same external `error`
  // slot the pre-migration hook used for both the GET and the toggle -- the
  // GET's own error surfaces via the query itself (see `error` below).
  const [mutationError, setMutationError] = useState<string | null>(null);

  const enabled = !!orgId && !!userId && !!features.switchRole;
  const queryKey = queryKeys.activeRole.dynamicRole(orgId || '', userId || '');

  const query = useQuery({
    queryKey,
    queryFn: () => fetchDynamicRole(orgId as string, userId as string),
    enabled,
    // Pre-migration refresh() had no retry wrapper.
    retry: false,
  });

  const data = query.data ?? DEFAULT_DATA;
  const isSwitch = data.dynamicRole === 'switch';

  const refresh = useCallback(async () => {
    setMutationError(null);
    await query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  const toggleMutation = useMutation({
    mutationFn: () => organizationAPI.toggleMyRole(orgId as string),
    // Pre-migration toggleRole() had no retry wrapper either.
    retry: false,
    onSuccess: (response) => {
      const toggled = unwrapResponse<{ dynamicRole: DynamicRole; activeRole: HelmRole; effectiveRole: HelmRole }>(
        response.data
      );
      if (toggled) {
        // toggleMyRole's response doesn't include switchSince -- preserve
        // whatever the GET last reported, matching pre-migration (which
        // never touched switchSince in this branch).
        queryClient.setQueryData<DynamicRoleData>(queryKey, (old: DynamicRoleData | undefined) => ({
          dynamicRole: toggled.dynamicRole,
          activeRole: toggled.activeRole,
          effectiveRole: toggled.effectiveRole,
          switchSince: old?.switchSince ?? null,
        }));
        // Invalidate all helm-related queries so dashboard re-renders with the new role
        queryClient.invalidateQueries({ queryKey: ['helm-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['helm-widgets'] });
        queryClient.invalidateQueries({ queryKey: ['helm-unread'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
    },
  });

  const toggleRole = useCallback(async () => {
    if (!orgId || !isSwitch) return;
    try {
      setMutationError(null);
      await toggleMutation.mutateAsync();
    } catch (err) {
      // bug-registry 0zzz-cx: RoleToggle.tsx's caller (PillarLayout.tsx)
      // never reads this hook's `error` field, so a rejected toggle was
      // caught, recorded, and read by nobody -- silent failure. Surface it
      // directly rather than relying on every future caller to opt in.
      const message = getErrorMessage(err);
      setMutationError(message);
      showError(message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, isSwitch, toggleMutation.mutateAsync]);

  const error = mutationError ?? (query.error ? getErrorMessage(query.error) : null);

  return {
    effectiveRole: data.effectiveRole,
    dynamicRole: data.dynamicRole,
    activeRole: data.activeRole,
    isSwitch,
    switchSince: data.switchSince,
    loading: query.isLoading,
    error,
    toggleRole,
    refresh,
  };
}
